import {
  DEFAULT_OCTANT_BANDS,
  HOR_CUTOFF_ANGLE_DEG,
  OCTANT_BAND_MIN,
  OCTANT_CUTOFF_ANGLE_MAX,
  OCTANT_CUTOFF_ANGLE_MIN,
  type OctantLengthOverrides,
  type OctantLengthUnit,
  OctantZone,
  SPACING_MAX_RADIUS_MIN,
  VERT_CUTOFF_ANGLE_DEG
} from "constants/octantZone.constants";

// Re-exported so consumers keep importing the zone type from the classifier
// module; it is declared in the constants module to keep that module and this
// one free of a circular import (see the note there).
export { OctantZone };

// Every zone, in the order the legend and the class filter list them.
export const OCTANT_ZONES: readonly OctantZone[] = [
  OctantZone.Lateral,
  OctantZone.Stack,
  OctantZone.Stagger
];

// One Model Engine "Spacing Features" parameter set, already resolved for the
// scene's display unit. The two band sizes are in scene units (the XDA backend
// converts the whole scene into the user's display units before responding, so
// deltas from centroids are compared against these directly); the two cutoff
// angles are in degrees and unit-free.
export interface OctantThresholds {
  readonly hMaxStack: number;
  readonly vMaxLateral: number;
  readonly horCutoffDeg: number;
  readonly vertCutoffDeg: number;
}

// Angle of a vector off an axis, in degrees: `across` is the component
// perpendicular to the axis, `along` the one down it.
//
// Angles are compared directly rather than converted to tan() slopes, which is
// what makes both ends of the range exact. `tan(90°)` is infinite — JavaScript
// returns 1.633e16 instead — so a slope comparison could not represent "the
// cone stops filtering", and at 0° it collapsed to "exactly on the axis" only
// as a side effect of multiplying by zero. Both are real settings (see
// OCTANT_CUTOFF_ANGLE_MIN/MAX), so neither can rest on an accident of
// arithmetic.
const degreesOffAxis = (across: number, along: number): number =>
  (Math.atan2(across, along) * 180) / Math.PI;

// Cone boundaries are inclusive, and the 45° diagonal is a boundary that
// decides real cases, so absorb float error rather than letting an atan2
// result of 45.00000000000001 fall outside a 45° cone.
const ANGLE_TOLERANCE_DEG = 1e-9;
const withinCone = (offAxisDeg: number, cutoffDeg: number): boolean =>
  offAxisDeg <= cutoffDeg + ANGLE_TOLERANCE_DEG;

/**
 * Classify a neighbour relative to the reference well.
 *
 * dx/dy are centroid deltas in scene units; dx must already have the layout
 * x-scale divided out (`xdaLayoutOptions.scale`, exactly like the ruler), and
 * sign is irrelevant.
 *
 * Each zone is a BAND intersected with a CONE, and both conditions apply
 * EVERYWHERE — the cone is not a tie-breaker consulted only where the two
 * bands overlap:
 *
 *   Lateral — `|Δvertical| ≤ Vert Max of Lateral` AND within the Hor Cutoff
 *             Angle of horizontal.
 *   Stack   — `|Δhorizontal| ≤ Hor Max of Stack` AND within the Vert Cutoff
 *             Angle of vertical.
 *   Stagger — everything else.
 *
 * That is what tapers each band to a point at the well in the octant diagram,
 * and it is what makes the two angles genuine controls rather than adjustments
 * to a small box around the origin: at 0° a cone closes and its class
 * disappears however generous the band, and at 90° a cone stops filtering so
 * the band alone decides, at full size.
 *
 * Boundaries are inclusive. Where both cones claim a neighbour — the diagonal
 * at 45°/45°, a whole wedge once the two angles sum past 90° — LATERAL wins,
 * being tested first.
 */
export function classifyOctantZone(
  dx: number,
  dy: number,
  thresholds: OctantThresholds
): OctantZone {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  if (
    ady <= thresholds.vMaxLateral &&
    withinCone(degreesOffAxis(ady, adx), thresholds.horCutoffDeg)
  ) {
    return OctantZone.Lateral;
  }
  if (
    adx <= thresholds.hMaxStack &&
    withinCone(degreesOffAxis(adx, ady), thresholds.vertCutoffDeg)
  ) {
    return OctantZone.Stack;
  }
  return OctantZone.Stagger;
}

/**
 * One widget's user-edited octant parameters (EVA-163), as they are held in
 * `XDASettingsT`. Every field is optional and every field is validated here
 * rather than trusted: the settings store is seeded VERBATIM from a saved
 * dashboard, so a value that never passed through the options panel — or one
 * saved before this feature existed — can still arrive.
 */
export interface OctantParameterOverrides {
  readonly lengths?: OctantLengthOverrides;
  readonly horCutoffDeg?: number;
  readonly vertCutoffDeg?: number;
}

// An override only counts if it is a real number in range; anything else
// (null, "", NaN from an emptied input, a negative band, an angle at or past
// the degenerate ends) falls through to the parameter's default.
const overrideOr = (
  value: number | undefined,
  fallback: number,
  min: number,
  max = Number.POSITIVE_INFINITY
): number =>
  typeof value === "number" && Number.isFinite(value) && value >= min && value <= max
    ? value
    : fallback;

/**
 * Resolve the Model Engine parameter set for the scene's display unit, with
 * the widget's own overrides applied on top.
 *
 * Each unit system carries its own canonical band sizes (EVA-29 AC: 150/400 ft
 * Imperial, 50/125 m Hybrid and SI), so the DEFAULTS are a table lookup, NOT a
 * unit conversion — nothing here needs eva-unit-core, and there is no
 * double-conversion hazard (the EVA-6715 bug class) to guard against. The XDA
 * length display unit is only ever "ft" or "m" (`BASE_UNITS.Length` is "m" and
 * `UNIT_TARGETS.Length` maps Hybrid/SI → "m", Imperial → "ft"), and "m" is also
 * what an uninitialised unit engine reports, so metric is the safe default for
 * anything unexpected.
 *
 * Overrides (EVA-163) follow the same rule and are therefore looked up under
 * the SAME unit key: a band the user typed in feet is only ever read back as
 * feet. The cutoff angles, being unit-free, are shared by both systems. The
 * neighbour-radius caps living in the same per-unit record are NOT read here
 * — they filter the display rather than the classification, and are resolved
 * by `resolveSpacingMaxRadii`.
 */
export function resolveOctantThresholds(
  displayUnit: string,
  overrides?: OctantParameterOverrides
): OctantThresholds {
  const unit: OctantLengthUnit = displayUnit === "ft" ? "ft" : "m";
  const defaults = DEFAULT_OCTANT_BANDS[unit];
  const bands = overrides?.lengths?.[unit];
  return {
    hMaxStack: overrideOr(bands?.hMaxStack, defaults.hMaxStack, OCTANT_BAND_MIN),
    vMaxLateral: overrideOr(bands?.vMaxLateral, defaults.vMaxLateral, OCTANT_BAND_MIN),
    horCutoffDeg: overrideOr(
      overrides?.horCutoffDeg,
      HOR_CUTOFF_ANGLE_DEG,
      OCTANT_CUTOFF_ANGLE_MIN,
      OCTANT_CUTOFF_ANGLE_MAX
    ),
    vertCutoffDeg: overrideOr(
      overrides?.vertCutoffDeg,
      VERT_CUTOFF_ANGLE_DEG,
      OCTANT_CUTOFF_ANGLE_MIN,
      OCTANT_CUTOFF_ANGLE_MAX
    )
  };
}

/**
 * Normalise the Class Filter (EVA-163) into the set of zones the spacing
 * overlay and the legend should show.
 *
 * An EMPTY or absent filter means every zone, not "no zones" — the filter is
 * a narrowing of the default display, and a widget saved before this feature
 * existed has no filter at all. That makes "show nothing" unreachable by
 * design: clearing the control returns to all classes, which is what the
 * "All classes" tag in the panel says it does.
 */
export function resolveVisibleZoneList(filter?: readonly OctantZone[]): OctantZone[] {
  // Driven off OCTANT_ZONES rather than the filter, which both normalises the
  // order (the legend must not reshuffle because of the order boxes were
  // ticked) and drops anything unrecognised out of a saved dashboard.
  const selected = OCTANT_ZONES.filter((zone) => (filter ?? []).includes(zone));
  return selected.length ? selected : [...OCTANT_ZONES];
}

/** Set form of {@link resolveVisibleZoneList}, for per-neighbour lookups. */
export function resolveVisibleZones(filter?: readonly OctantZone[]): Set<OctantZone> {
  return new Set(resolveVisibleZoneList(filter));
}

/**
 * Neighbour-radius caps (EVA-163) for the scene's display unit — the
 * "avoid crowding the widget with labels" control, NOT a classification input.
 * A neighbour beyond either cap is simply not drawn; its zone is unchanged.
 *
 * `Infinity` is the unset value, and deliberately so: absent means "no limit",
 * which reproduces the EVA-29 display exactly, so a widget saved before this
 * feature existed labels every neighbour as it always did. Comparisons against
 * Infinity are well-defined, which keeps the caller free of null checks.
 *
 * Read under the same unit key as the bands, and never converted — see the
 * note on `OctantLengthOverrides`.
 */
export interface SpacingMaxRadii {
  /** Radial: a neighbour is dropped when `hypot(dx, dy)` exceeds this. */
  readonly neighbor: number;
  /** Dropped when `|dy|` exceeds this, clipping the radial circle flat. */
  readonly vertical: number;
}

export function resolveSpacingMaxRadii(
  displayUnit: string,
  lengths?: OctantLengthOverrides
): SpacingMaxRadii {
  const unit: OctantLengthUnit = displayUnit === "ft" ? "ft" : "m";
  const forUnit = lengths?.[unit];
  return {
    neighbor: overrideOr(
      forUnit?.maxNeighborRadius,
      Number.POSITIVE_INFINITY,
      SPACING_MAX_RADIUS_MIN
    ),
    vertical: overrideOr(
      forUnit?.maxVerticalDistance,
      Number.POSITIVE_INFINITY,
      SPACING_MAX_RADIUS_MIN
    )
  };
}

/**
 * Is this neighbour close enough to the reference well to be labelled?
 *
 * `dx` must already have the layout x-scale divided out, exactly as for
 * `classifyOctantZone` — the radial term mixes the two components, so an
 * unscaled dx would distort the circle into an ellipse that changes with the
 * Scale Width setting.
 */
export function isWithinSpacingMaxRadii(
  dx: number,
  dy: number,
  maxRadii: SpacingMaxRadii
): boolean {
  return Math.abs(dy) <= maxRadii.vertical && Math.hypot(dx, dy) <= maxRadii.neighbor;
}
